import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {config} from "../../../base/constant";
import {RootState} from "../../../base/store/store";

export const propertyDetailApi = createApi({
    reducerPath: "propertyDetail",
    baseQuery: fetchBaseQuery({
        baseUrl: config().BASE_URL,
        prepareHeaders: (headers: any) => {
            headers.set("Authorization", `Token ${localStorage.getItem("token")}`);
            return headers;
        },
    }),
    tagTypes: ["property-detail"],
    endpoints: (builder) => ({

        getMyPropertyDetails: builder.query<any, void>({
            query: () => ({
                url: "/my-project/",
                method: "GET",
            }),
            providesTags: ["property-detail"],
        }),

        getProjectMilestone: builder.query<any, void>({
            query: () => ({
                url: '/project-milestone/',
                method: "GET"
            }),
        }),

        getPropertyServices: builder.query<any, void>({
            query: () => ({
                url: "/customer-property-services/",
                method: "GET",
            }),
        }),

        getSupportTeamDetails: builder.query<any, void>({
            query: () => ({
                url: "/customer-support-team/",
                method: "GET",
            }),
        }),

        getPropertyDocuments: builder.query<any, void>({
            query: () => ({
                url: "/customer-project-documents/",
                method: "GET",
            }),
        }),

        reqDoc: builder.mutation<any, any>({
            query: (data) => ({
                url: "/request-documents/",
                method: "POST",
                body: data,
            }),
        }),
    }),
});

export const {
    useGetMyPropertyDetailsQuery,
    useGetProjectMilestoneQuery,
    useGetPropertyServicesQuery,
    useGetSupportTeamDetailsQuery,
    useGetPropertyDocumentsQuery,
    useReqDocMutation,
} = propertyDetailApi;

const rawProgressBaseQuery = fetchBaseQuery({
    baseUrl: config().SALESFORCE_URL,
    prepareHeaders: (headers, { getState }) => {

        const token = (getState() as RootState).salesforceAuth.token;

        if (token) {
            headers.set("Authorization", token.startsWith("Bearer ") ? token : `Bearer ${token}`);
        } else {
            headers.set("Authorization", "Bearer 00D2w00000RHVzI!AQEAQCFHvgzxSFgeBmtmISRRKbXKx4OSow2a57t_0XA.sJ.FUE7KQpsWWS_jz5dRxVGzQtJl_jZ3kvdJA3z08Lku1u81jTHF");
        }

        headers.set("Content-Type", "application/json");

        return headers;
    }
});

export const progressDocumentsApi = createApi({
    reducerPath: "progressDocuments",
    baseQuery: async (args: any, api: any, extraOptions: any) => {
        const startTime = performance.now();
        const urlStr = typeof args === "string" ? args : args.url;
        const method = (typeof args === "object" && args.method) || "POST";
        const body = (typeof args === "object" && args.body) || {};

        const result = await rawProgressBaseQuery(args, api, extraOptions);
        const durationMs = Math.round(performance.now() - startTime);

        const dataObj = result.data as any;
        const errObj = result.error as any;

        const isSuccess = !result.error && (!dataObj || (dataObj.success !== false && dataObj.status !== "failed"));
        const statusCode = result.error ? (typeof errObj?.status === 'number' ? errObj.status : 500) : 200;

        let apiName = "SALESFORCE_API";
        let summary = "";
        if (urlStr.includes("easebuzz/payment")) {
            apiName = "EASEBUZZ_PAYMENT";
            summary = `Payment initiation: Opportunity ${body.opportunityId || '-'}, Amount ₹${body.amount || '-'}`;
        } else if (urlStr.includes("progressDocuments")) {
            apiName = "PROGRESS_DOCUMENTS";
            summary = `Progress docs for ${body.project_name || '-'}`;
        } else if (urlStr.includes("progressInventory")) {
            apiName = "PROGRESS_INVENTORY";
            summary = `Progress inventory for ${body.project_name || '-'}`;
        }

        try {
            const { recordSalesforceApiAudit, getStoredCustomerInfo } = await import("../sales-force/sfAuditLogger");
            const user = getStoredCustomerInfo();
            recordSalesforceApiAudit({
                api_name: apiName,
                endpoint_url: urlStr,
                method,
                user_phone: body.customerMobile || user.phone,
                user_email: user.email,
                user_name: user.name,
                request_payload: body,
                response_status: isSuccess ? "SUCCESS" : "FAILED",
                status_code: statusCode,
                response_summary: summary,
                response_data: dataObj || null,
                error_message: errObj ? JSON.stringify(errObj.data || errObj) : (dataObj?.message || ""),
                response_time_ms: durationMs,
            });
        } catch (e) {
            // ignore
        }

        return result;
    },
    tagTypes: ["progress-documents"],
    endpoints: (builder) => ({
        postProgressDocuments: builder.mutation<any, any>({
            query: (customerInfo) => ({
                url: "/progressDocuments",
                method: "POST",
                body: customerInfo
            }),
            invalidatesTags: ["progress-documents"],
        }),
        postProgressInventory: builder.mutation<any, any>({
            query: (customerInfo) => ({
                url: "/progressInventory",
                method: "POST",
                body: customerInfo
            }),
            invalidatesTags: ["progress-documents"],
        }),
        postEasebuzzPayment: builder.mutation<any, any>({
            query: (paymentInfo) => ({
                url: config().SALESFORCE_EASEBUZZ_PAYMENT_URL || "https://casagrandbuilderprivatelimited.my.salesforce.com/services/apexrest/easebuzz/payment/",
                method: "POST",
                body: paymentInfo,
            }),
        }),
    }),
});

export const {
    usePostProgressDocumentsMutation,
    usePostProgressInventoryMutation,
    usePostEasebuzzPaymentMutation,
} = progressDocumentsApi;
